import React, {FC, useEffect, useLayoutEffect, useRef, useState} from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import moment from "moment";
import {useParams} from "react-router-dom";
import BigNumber from "bignumber.js";
import {CONSTANT} from "../../../common/utils/constants";
import {formatNumberWithCommas} from "../../../common/utils/function";
import {useSelector} from "react-redux";
import {chartService} from "../../../store/actions";

const CoinBalance: FC = () => {
    const params = useParams();
    const _stats = useSelector((e: Object) => (e as any)?.common?.stats);
    const [loading, setLoading] = useState(true);
    const [options, setOptions] = useState({
        chart: {
            plotBackgroundColor: null,
            plotBorderWidth: null,
            plotShadow: false,
            type: "areaspline",
            zoomType: "",
            height: 500,
            spacingLeft: 0,
            spacingRight: 0,
        },
        credits: {enabled: false},
        title: {
            x: 30,
            align: "left",
            useHTML: true,
            text: "Balance",
            style: {color: "#a3a3a3"},
        },
        subtitle: {
            y: -1,
            useHTML: true,
            align: "center",
            text: `
                <table style="width: 100%; text-align: center;">
                <tr>
                    <td style="font-weight: bold;">Balance for ${params.address}</td>
                </tr>
                <tr>
                    <td>Source: ${process.env.REACT_APP_BASE_EXPLORER || "Blocksscan"}.io</td>
                </tr>
                </table>
            `,
        },
        tooltip: {
            useHTML: true,
            formatter: function (this: Highcharts.Point): string {
                const dateFormatted = moment(this.x).format("dddd, MMMM DD, YYYY");
                const y = this.y ?? 0;
                const balanceFormatted = parseFloat(y.toFixed(2));
                const usdPrice = _stats?.coin_price ? (balanceFormatted * _stats?.coin_price).toFixed(2) : 'N/A';

                const tooltipHTML = `
            <table class="text-dark">
                <tr><td style="font-weight:bold;">Date:</td><td>${dateFormatted}</td></tr>
                <tr><td style="font-weight:bold;">Balance:</td><td>${formatNumberWithCommas(balanceFormatted.toString())}</td></tr>
                <tr><td style="font-weight:bold;">USD:</td><td>$${usdPrice}</td></tr>
            </table>
        `;

                return tooltipHTML;
            }
        },

        marker: {
            radius: 2,
        },
        plotOptions: {
            series: {
                color: `#${CONSTANT.CHART_COLOUR}`,
                lineWidth: "2px",
                fillColor: {
                    linearGradient: {x1: 0, x2: 0, y1: 0, y2: 1},
                    stops: [
                        [0, `#${CONSTANT.CHART_COLOUR}66`],
                        [0.6, `#${CONSTANT.CHART_COLOUR}00`],
                        // [1, `#${CONSTANT.CHART_COLOUR}00`],
                    ],
                },
            },
        },
        xAxis: {
            type: "datetime",
            visible: true,
            title: {
                text: "Date",
            },
            categories: [],
            lineWidth: 0,
            minorGridLineWidth: 0,
            lineColor: "transparent",
            minorTickLength: 0,
            labels: {
                formatter: function (this: any) {
                    return moment(this.value).format("DD MMM");
                },
                padding: 0,
            },
            tickLength: 0,
            tickInterval: 5 * 24 * 60 * 60 * 1000,
        },
        yAxis: {
            title: {text: "Balance"},
            lineWidth: 0,
            gridLineWidth: 0,
            minorGridLineWidth: 0,
            lineColor: "transparent",
            labels: {
                autoRotation: false,
                padding: 0,
            },
            minorTickLength: 0,
            tickLength: 0,
        },
        legend: {enabled: false},
        series: [{
            name: "Balance",
            data: [],
            marker: {enabled: false},
        }],
    });
    const chartRef = useRef<HighchartsReact.RefObject>(null);

    useEffect(() => {
        if (chartRef.current) {
            const chart = chartRef.current.chart;

            const logoUrl = `${process.env.REACT_APP_BASE_URL}/eaas/img/${process.env.REACT_APP_BASE_UNIT}-dark-logo.svg`;
            const logoWidth = 100;
            const logoHeight = 30;

            const existingLogo = chart.renderer.box.getElementsByClassName("chart-brand-logo");
            if (existingLogo.length) {
                Array.from(existingLogo).forEach((elem) => elem.remove());
            }

            chart.renderer
                .image(
                    logoUrl,
                    chart.plotLeft + (chart.plotWidth - logoWidth) / 2,
                    chart.plotTop + (chart.plotHeight - logoHeight) / 2,
                    logoWidth,
                    logoHeight
                )
                .addClass("chart-brand-logo")
                .add();
        }
    }, [options]);

    const getChartData = async () => {
        try {
            setLoading(true);

            chartService.getBalanceHistory(params.address).then(res => {
                const data = res.items;

                const seriesData = data.map((item: any) => [
                    moment(item.day, 'YYYY-MM-DD').valueOf(),
                    BigNumber(item.value).div(10 ** 18).toNumber()
                ]);

                setOptions(prevOptions => ({
                    ...prevOptions,
                    series: [{
                        ...prevOptions.series[0],
                        data: seriesData
                    }]
                }));
            }).catch(e => {

            })
        } catch (error) {
            console.error("Error fetching chart data:", error);
        } finally {
            setLoading(false);
        }
    };

    useLayoutEffect(() => {
        getChartData();
    }, [params.address]);

    return (
        <div>
            {loading ? <p>Loading...</p> : <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef}/>}
        </div>
    );
};

export default CoinBalance;
